import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap-v5';
import { NumericFormat } from 'react-number-format';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { KTSVG, LoadingGrid, SimpleAlert } from '../../../../rbts/helpers';
import { ProductsList } from './ProductsList';
import { crearPuntoVentaService } from '../../../service/start_shift/StartShift';
import { useObtenerPVPorID } from '../hooks/StartShiftHook';

type Props = {
  show: boolean;
  handleClose: (refresh: boolean) => void;
  id: number;
};

const PuntoVentaSchema = Yup.object().shape({
  nombre: Yup.string()
    .trim()
    .test(
      'not-only-spaces',
      'El nombre del evento no puede contener solo espacios.',
      (value) => value?.trim() !== ''
    )
    .required('Nombre del evento es requerido.'),
});

const ModalCE: React.FC<Props> = ({ show, handleClose, id }) => {
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [loading, setLoading] = useState(false);
  const [listaProductos, setListaProductos] = useState([]);

  const { initValues, loadingRequest: loadingReq } = useObtenerPVPorID(id);

  const formPuntoVenta = useFormik({
    initialValues: initValues,
    validationSchema: PuntoVentaSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      values.productos = listaProductos;

      const productNames = values.productos.map((p: any) => p.nombre?.trim().toLowerCase()).filter(Boolean);
      if (productNames.length !== new Set(productNames).size) {
        SimpleAlert('No se permiten productos con el mismo nombre.', 3000, 'error');
        setSubmitting(false);
        return;
      }

      const hasLongName = values.productos.some(
        (p: any) => p.nombre && p.nombre.length > 800
      );
      if (hasLongName) {
        SimpleAlert('El nombre del producto no puede superar los 800 caracteres.', 3000, 'error');
        setSubmitting(false);
        return;
      }

      const hasInvalidCosto = values.productos.some(
        (p: any) => !p.aplica_costo_fijo_cortesia && (!p.precio_venta || p.precio_venta === 0)
      );
      if (hasInvalidCosto) {
        SimpleAlert('Los productos sin cortesía deben tener un costo mayor a $0.00.', 3000, 'error');
        setSubmitting(false);
        return;
      }

      const hasInvalidCortesia = values.productos.some(
        (p: any) => p.aplica_costo_fijo_cortesia && (!p.costo_fijo_cortesia || p.costo_fijo_cortesia === 0)
      );
      if (hasInvalidCortesia) {
        SimpleAlert('Los productos con cortesía deben tener un costo fijo mayor a $0.00.', 3000, 'error');
        setSubmitting(false);
        return;
      }

      setLoading(true);
      try {
        const res = await crearPuntoVentaService(values);
        SimpleAlert(res.data.message, 3000, 'success');
        formPuntoVenta.resetForm();
        setListaProductos([]);
        handleClose(true);
        setLoading(false);
      } catch (error: any) {
        if (error && error.response) {
          const { message } = error.response.data;
          SimpleAlert(message, 3000, 'error');
          setLoading(false);
        }
      }
    },
  });

  const changeSplit = (values: any) => {
    const { floatValue } = values;
    let porcentaje_banco = floatValue || 0;
    let porcentaje_punto_venta = 100 - porcentaje_banco;
    formPuntoVenta.setFieldValue('banco', porcentaje_banco);
    formPuntoVenta.setFieldValue('punto_venta', porcentaje_punto_venta);
  };
  const changeChecked = (e: any) => {
    const isChecked = e.target.checked;
    formPuntoVenta.setFieldValue('is_supply_available', isChecked);
  };

  return (
    <Modal
      id="kt_modal_create"
      tabIndex={-1}
      aria-hidden="true"
      backdrop="static"
      dialogClassName="modal-dialog mw-1000px"
      show={show}
      onHide={() => {
        formPuntoVenta.resetForm();
        handleClose(false);
      }}
    >
      <div className="modal-content">
        <div className="modal-header">
          <h2 className="text-muted">
            {id > 0 ? 'Editar punto de venta' : 'Nuevo punto de venta'}
          </h2>
          <div
            className="btn btn-sm btn-icon btn-active-color-primary"
            onClick={() => {
              formPuntoVenta.resetForm();
              handleClose(false);
            }}
          >
            <KTSVG
              className="svg-icon-2"
              path="/media/icons/duotune/arrows/arr061.svg"
            />
          </div>
        </div>
        <div className="modal-body mx-5 padding-top-00">
          <form
            id="kt_modal_generic_form"
            className="form"
            action="#"
            onSubmit={formPuntoVenta.handleSubmit}
          >
            <div className="row wh-0100 mb-3">
              <div className="col-xxl-6 col-xl-6 col-md-6 col-lg-6 mt-5">
                <div className="w-100 d-flex">
                  <label className="required fw-bold fs-6 mb-2 w-auto">
                    Nombre
                  </label>
                </div>
                <input
                  className="form-control mb-2 w-100  padd-left input-name-form"
                  data-kt-element="input"
                  placeholder="Ingrese el nombre del evento"
                  maxLength={40}
                  {...formPuntoVenta.getFieldProps('nombre')}
                ></input>
                {formPuntoVenta.touched.nombre && formPuntoVenta.errors.nombre && (
                  <div className="fv-plugins-message-container text-danger w-100">
                    <span role="alert">{formPuntoVenta.errors.nombre}</span>
                  </div>
                )}
              </div>
              <div className="col-xxl-6 col-xl-6 col-md-6 col-lg-6 mt-5">
                <div className="form-check form-check-custom form-check-solid mt-12">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="kt_login_toc_agree"
                    checked={formPuntoVenta.values.is_supply_available}
                    onChange={changeChecked}
                  />
                  <label
                    className="form-check-label fw-bold text-gray-700 fs-6"
                    htmlFor="kt_login_toc_agree"
                  >
                    Habilitar suministro de productos
                  </label>
                </div>
              </div>
              <div className="col-xxl-6 col-xl-6 col-md-6 col-lg-6 mt-5">
                <div className="w-100 d-flex">
                  <label className="required fw-bold fs-6 mb-2 w-auto">
                    Split de banco
                  </label>
                </div>
                <NumericFormat
                  type="text"
                  autoComplete="off"
                  className="form-control"
                  value={formPuntoVenta.values.banco}
                  decimalScale={0}
                  decimalSeparator="."
                  fixedDecimalScale
                  placeholder={'0 %'}
                  disabled={false}
                  suffix={' %'}
                  isAllowed={(values: any) => {
                    const { floatValue } = values;
                    let allow = floatValue <= 100 || floatValue === undefined;
                    return allow;
                  }}
                  allowNegative={false}
                  thousandSeparator={''}
                  onValueChange={changeSplit}
                />

                {/* {formPuntoVenta.touched.fecha_inicio &&
                  formPuntoVenta.errors.fecha_inicio && (
                    <div className="fv-plugins-message-container text-danger w-100">
                      <span role="alert">{formPuntoVenta.errors.fecha_inicio}</span>
                    </div>
                  )} */}
              </div>
              <div className="col-xxl-6 col-xl-6 col-md-6 col-lg-6  mt-5">
                <div className="w-100 d-flex">
                  <label className="required fw-bold fs-6 mb-2 w-auto">
                    Split de punto de venta
                  </label>
                </div>
                <NumericFormat
                  type="text"
                  autoComplete="off"
                  className="form-control"
                  value={formPuntoVenta.values.punto_venta}
                  decimalScale={0}
                  decimalSeparator="."
                  fixedDecimalScale
                  placeholder={'0 %'}
                  disabled={true}
                  suffix={' %'}
                  allowNegative={false}
                />
                {/* {formPuntoVenta.touched.fecha_fin && formPuntoVenta.errors.fecha_fin && (
                  <div className="fv-plugins-message-container text-danger w-100">
                    <span role="alert">{formPuntoVenta.errors.fecha_fin}</span>
                  </div>
                )} */}
              </div>
            </div>
            <div className="row wh-0100 mb-3">
              <div className="col-xxl-12 col-xl-12 col-md-12 col-lg-12"></div>
            </div>
            <div className="row wh-0100 mb-3">
              <div className="col-xxl-12 col-xl-12 col-md-12 col-lg-12">
                {loadingRequest ? (
                  <LoadingGrid text="Cargando..."></LoadingGrid>
                ) : (
                  <ProductsList
                    data={initValues.productos}
                    onGetDataProducts={(productos) => {
                      setListaProductos(
                        productos.filter(
                          (obj: any) => obj.nombre && obj.nombre.trim() !== ''
                        )
                      );
                    }}
                  />
                )}
              </div>
            </div>
            <div className="d-flex justify-content-end mt-10">
              <button
                type="reset"
                className="btn btn-light me-3"
                data-kt-users-modal-action="cancel"
                onClick={() => {
                  formPuntoVenta.resetForm();
                  handleClose(false);
                }}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                data-kt-users-modal-action="submit"
                disabled={loading}
              >
                {!loading && <span className="indicator-label">Guardar</span>}
                {loading && (
                  <span
                    className="indicator-progress"
                    style={{ display: 'block' }}
                  >
                    Espere por favor...
                    <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                  </span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};
export { ModalCE };
